// Pure request-payload builders and analytics side-effects carved out of
// controllers/index.js. No `this`: every input is an explicit parameter.

import moment from 'moment-timezone';

const WAITING_LIST_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// Maps the positional monday..sunday checkbox flags onto the named day keys the
// waitinglist endpoint expects.
export function buildWaitingListPayload({
  me,
  productSelected,
  contactSelected,
  arrWaitListDays,
  waitListNotes,
}) {
  const days = WAITING_LIST_DAYS.reduce((acc, day, index) => {
    acc[day] = arrWaitListDays[index]?.value ?? false;
    return acc;
  }, {});

  return {
    ...(me?.id && { contact_id: me.id }),
    ...(productSelected?.id && { product_id: productSelected.id }),
    ...(contactSelected?.id && { specialist_id: contactSelected.id }),
    ...days,
    notes: waitListNotes ?? '',
  };
}

export function buildConfirmBookingPayload({
  me,
  people,
  voucher,
  voucherToBuy,
  contactSelected,
  productSelected,
  hourSelected,
  selectedTimeZone,
  organizationTermOptions,
  organizationName,
  thumbnailUrl,
  primaryColor,
  userId,
  lang,
}) {
  const address = me.addresses?.[0] || {};

  return {
    is_voucher: voucherToBuy ? true : false,
    voucher: voucher,
    contact_id: contactSelected?.id || null,
    contact_name: contactSelected?.name || null,
    contact_mail: contactSelected?.mail || null,
    product_id: productSelected?.id || null,
    product_name: productSelected?.name || null,
    first_name: me.first_name,
    last_name: me.last_name,
    password: me.password,
    piva: me.piva,
    ragione_sociale: me.ragione_sociale || '',
    fisica_giuridica: me.fisica_giuridica ? true : false,
    mobile: me.mobile,
    mail: me.mail,
    pec: me.pec,
    codice_destinatario: me.codice_destinatario,
    cod_fiscale: me.cod_fiscale,
    data_nascita: me.data_nascita,
    luogo_nascita: me.luogo_nascita,
    location_address_street: address.location_address_street || null,
    location_address_house: address.location_address_house || null,
    location_address_postal_code: address.location_address_postal_code || null,
    location_address_city: address.location_address_city || null,
    location_address_country: address.location_address_country || null,
    location_address_country_code: address.location_address_country_code || null,
    user_id: userId,
    event_group_id: voucherToBuy ? null : productSelected?.is_group ? hourSelected.id : null,
    from_date: voucherToBuy
      ? null
      : moment(hourSelected.start).tz(selectedTimeZone).format('YYYY-MM-DDTHH:mmZ'),
    to_date: voucherToBuy
      ? null
      : moment(hourSelected.end).tz(selectedTimeZone).format('YYYY-MM-DDTHH:mmZ'),
    stanza_id: voucherToBuy ? null : hourSelected.stanza_id || null,
    people: people,
    organization_term_options: organizationTermOptions,
    organization_name: organizationName,
    thumbnail_url: thumbnailUrl,
    primary_color: primaryColor,
    lang: lang,
  };
}

// Fires the gtag + dataLayer purchase events for a confirmed booking.
//
// The gtag call MUST stay guarded: `confirmBookingTask` calls this from inside
// its try block, before `bookConfirmed` is set, so an unguarded `gtag(...)` on
// a page where the snippet is missing (blocked by an extension, stripped by an
// embedding host) throws a ReferenceError that the task's catch swallows — the
// user gets neither a confirmation nor an error for a booking the server has
// already accepted. Same guard as beebee-base/lib/router-tracking.js.
export function trackPurchase(productSelected) {
  if (typeof gtag === 'function') {
    gtag('event', 'purchase', {
      event_category: 'ecommerce',
      event_label: productSelected,
      send_to: 'G-Q4R8QL4WC0',
    });
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        items: [
          {
            item_id: productSelected ? productSelected.id : null,
            item_name: productSelected ? productSelected.name : null,
            price: productSelected ? productSelected.price : null,
            quantity: productSelected ? productSelected.quantity || 1 : 1,
          },
        ],
      },
    });
  }
}
